import { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Paper,
  Breadcrumbs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Grid,
} from '@mui/material';
import { useNotification } from '../../../components/Notification';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  NavigateNext as NavigateNextIcon,
} from '@mui/icons-material';
import Table from '../../../components/Table';
import Input from '../../../components/Input';
import AddEntryExitStatusModal from './AddEntryExitStatusModal';
import { hasPermission } from '../../../utils/permissions';
import {
  fetchEntryExitStatuses,
  fetchEntryExitStatusById,
  addEntryExitStatus,
  updateEntryExitStatus,
  selectEntryExitStatuses,
  selectEntryExitStatusesLoading,
  selectEntryExitStatusesError,
  selectEntryExitStatusesSuccessMessage,
  selectEntryExitStatusesTotal,
  selectEntryExitStatusesPage,
  selectEntryExitStatusesPageSize,
  selectEntryExitStatusesSortColumn,
  selectEntryExitStatusesSortDirection,
  selectEntryExitStatusesFilterName,
  selectSelectedEntryExitStatus,
  setPage,
  setPageSize,
  setSortColumn,
  setSortDirection,
  setFilterName,
  deleteEntryExitStatus,
  bulkDeleteEntryExitStatuses,
  clearError,
  clearSuccessMessage,
} from './slice';

function EntryExitStatuses({ onSelectedIdsChange, onAddClick, onBulkDeleteClick, hideButtons = false, isActive = true }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showNotification } = useNotification();

  const entryExitStatuses = useSelector(selectEntryExitStatuses);
  const loading = useSelector(selectEntryExitStatusesLoading);
  const error = useSelector(selectEntryExitStatusesError);
  const successMessage = useSelector(selectEntryExitStatusesSuccessMessage);
  const total = useSelector(selectEntryExitStatusesTotal);
  const page = useSelector(selectEntryExitStatusesPage);
  const pageSize = useSelector(selectEntryExitStatusesPageSize);
  const sortColumn = useSelector(selectEntryExitStatusesSortColumn);
  const sortDirection = useSelector(selectEntryExitStatusesSortDirection);
  const filterName = useSelector(selectEntryExitStatusesFilterName);
  const selectedEntryExitStatus = useSelector(selectSelectedEntryExitStatus);

  const [selectedIds, setSelectedIds] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [entryExitStatusToDelete, setEntryExitStatusToDelete] = useState(null);
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);

  // Check permissions - hideButtons only affects top action buttons, not row actions
  const canAddEntryExitStatus = hasPermission('entryexitstatus', 'addEntryExitStatus');
  const canEditEntryExitStatus = hasPermission('entryexitstatus', 'editEntryExitStatus');
  const canDeleteEntryExitStatus = hasPermission('entryexitstatus', 'deleteEntryExitStatus');
  
  // Handler for selection change that also notifies parent
  const handleSelectionChange = useCallback((ids) => {
    setSelectedIds(ids);
    // Only notify parent if callback is provided and we're in embedded mode
    if (onSelectedIdsChange && hideButtons) {
      onSelectedIdsChange(ids);
    }
  }, [onSelectedIdsChange, hideButtons]);
  
  // Use local selectedIds
  const effectiveSelectedIds = selectedIds;

  // Fetch data whenever parent tab changes or when filters change
  useEffect(() => {
    dispatch(fetchEntryExitStatuses({ page, pageSize, sortColumn, sortDirection, name: filterName }));
  }, [dispatch, page, pageSize, sortColumn, sortDirection, filterName, isActive]);

  // Show error notification
  useEffect(() => {
    if (error) {
      showNotification(error, 'error');
      dispatch(clearError());
    }
  }, [error, dispatch, showNotification]);

  // Show success notification
  useEffect(() => {
    if (successMessage) {
      showNotification(successMessage, 'success');
      dispatch(clearSuccessMessage());
      dispatch(fetchEntryExitStatuses({ page, pageSize, sortColumn, sortDirection, name: filterName }));
    }
  }, [successMessage, dispatch, showNotification, page, pageSize, sortColumn, sortDirection, filterName]);

  // Define table columns
  const columns = [
    {
      key: 'name',
      label: t('entryExitStatuses.table.name'),
    },
    {
      key: 'description',
      label: t('entryExitStatuses.table.description'),
    },
  ];

  // Handle edit action
  const handleEdit = (row) => {
    dispatch(fetchEntryExitStatusById(row.id));
    setShowEditDialog(true);
  };

  // Handle delete action
  const handleDelete = (row) => {
    setEntryExitStatusToDelete(row);
    setShowDeleteDialog(true);
  };

  // Confirm delete action
  const handleConfirmDelete = () => {
    if (entryExitStatusToDelete?.id) {
      dispatch(deleteEntryExitStatus(entryExitStatusToDelete.id));
    }
    setShowDeleteDialog(false);
    setEntryExitStatusToDelete(null);
  };

  // Cancel delete action
  const handleCancelDelete = () => {
    setShowDeleteDialog(false);
    setEntryExitStatusToDelete(null);
  };

  // Handle bulk delete
  const handleBulkDelete = () => {
    if (onBulkDeleteClick) {
      onBulkDeleteClick();
    } else {
      setShowBulkDeleteDialog(true);
    }
  };

  // Confirm bulk delete action
  const handleConfirmBulkDelete = () => {
    dispatch(bulkDeleteEntryExitStatuses(effectiveSelectedIds));
    setShowBulkDeleteDialog(false);
    setSelectedIds([]);
  };

  // Cancel bulk delete action
  const handleCancelBulkDelete = () => {
    setShowBulkDeleteDialog(false);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    dispatch(setPageSize(newPageSize));
  };

  // Handle sort change
  const handleSortChange = (column, direction) => {
    dispatch(setSortColumn(column));
    dispatch(setSortDirection(direction));
  };

  // Handle filter change
  const handleFilterChange = (event) => {
    dispatch(setFilterName(event.target.value));
  };

  // Handle add entry exit status
  const handleAddEntryExitStatus = () => {
    if (onAddClick) {
      onAddClick();
    } else {
      setShowAddDialog(true);
    }
  };

  // Handle close add entry exit status dialog
  const handleCloseAddEntryExitStatus = () => {
    setShowAddDialog(false);
  };

  // Confirm add entry exit status
  const handleConfirmAddEntryExitStatus = (entryExitStatusData) => {
    dispatch(addEntryExitStatus(entryExitStatusData));
    setShowAddDialog(false);
  };

  // Handle close edit entry exit status dialog
  const handleCloseEditEntryExitStatus = () => {
    setShowEditDialog(false);
  };

  // Confirm edit entry exit status
  const handleConfirmEditEntryExitStatus = (entryExitStatusData) => {
    dispatch(updateEntryExitStatus(entryExitStatusData));
    setShowEditDialog(false);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, overflow: 'hidden' }}>
      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#ffffff', minHeight: 0 }}>
        {/* Action Buttons - Only show if not hidden */}
        {!hideButtons && (
          <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            {canAddEntryExitStatus && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleAddEntryExitStatus}
                id="addEntryExitStatus"
                name="addEntryExitStatus"
                data-testid="entryExitStatuses-button-addEntryExitStatus"
              >
                {t('entryExitStatuses.addEntryExitStatus')}
              </Button>
            )}
            {canDeleteEntryExitStatus && (
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleBulkDelete}
                disabled={effectiveSelectedIds.length === 0}
                id="deleteSelected"
                name="deleteSelected"
                data-testid="entryExitStatuses-button-deleteSelected"
              >
                {t('entryExitStatuses.deleteSelected')}
              </Button>
            )}
          </Box>
        )}
        {/* Filter Section */}
        <Box sx={{ px: 3, pt: 2, pb: 2, borderBottom: '1px solid #e5e7eb', flexShrink: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <Input
                label={t('entryExitStatuses.filterByName')}
                value={filterName}
                onChange={handleFilterChange}
                placeholder={t('entryExitStatuses.filterByNamePlaceholder')}
                id="filterName"
                name="filterName"
                data-testid="entryExitStatuses-input-filterName"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Table */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, pt: 2, pb: 3, backgroundColor: '#ffffff', minHeight: 0 }}>
          <Table
            id="entryExitStatuses-table"
            columns={columns}
            data={entryExitStatuses}
            selectable={!!onBulkDeleteClick}
            selectedIds={effectiveSelectedIds}
            onSelectionChange={handleSelectionChange}
            onEdit={canEditEntryExitStatus ? handleEdit : null}
            onDelete={canDeleteEntryExitStatus ? handleDelete : null}
            loading={loading}
            emptyMessage={t('entryExitStatuses.noData')}
            idField="id"
            pagination={true}
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 50, 100]}
            sortable={true}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            sortableColumns={['name', 'description']}
          />
        </Box>
      </Paper>

      {/* Confirm Delete Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('entryExitStatuses.deleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('entryExitStatuses.deleteDialog.message', {
              name: entryExitStatusToDelete?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            id="cancelDelete"
            name="cancelDelete"
            data-testid="entryExitStatuses-button-cancelDelete"
          >
            {t('entryExitStatuses.deleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmDelete"
            name="confirmDelete"
            data-testid="entryExitStatuses-button-confirmDelete"
          >
            {t('entryExitStatuses.deleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Bulk Delete Dialog */}
      <Dialog
        open={showBulkDeleteDialog}
        onClose={handleCancelBulkDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'error.main',
          }}
        >
          <DeleteIcon />
          {t('entryExitStatuses.bulkDeleteDialog.title')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('entryExitStatuses.bulkDeleteDialog.message', {
              count: effectiveSelectedIds.length,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelBulkDelete}
            variant="outlined"
            id="cancelBulkDelete"
            name="cancelBulkDelete"
            data-testid="entryExitStatuses-button-cancelBulkDelete"
          >
            {t('entryExitStatuses.bulkDeleteDialog.cancel')}
          </Button>
          <Button
            onClick={handleConfirmBulkDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            id="confirmBulkDelete"
            name="confirmBulkDelete"
            data-testid="entryExitStatuses-button-confirmBulkDelete"
          >
            {t('entryExitStatuses.bulkDeleteDialog.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Entry Exit Status Modal */}
      <AddEntryExitStatusModal
        open={showAddDialog}
        onClose={handleCloseAddEntryExitStatus}
        onConfirm={handleConfirmAddEntryExitStatus}
      />

      {/* Edit Entry Exit Status Modal */}
      <AddEntryExitStatusModal
        open={showEditDialog}
        onClose={handleCloseEditEntryExitStatus}
        onConfirm={handleConfirmEditEntryExitStatus}
        entryExitStatusData={selectedEntryExitStatus}
        editMode={true}
      />
    </Box>
  );
}

export default EntryExitStatuses;

